import React from "react";

const COMPANY = {
  name: "EasyIce SRLS",
  vat: "03618290781",
  address: "Via Ugo D'Atri 5, 87012 Castrovillari (CS)",
  admin: "Ivan Loscialpo",
  email: "easyicecalabria@gmail.com",
};

const LegalLayout = ({ title, updated, children }) => (
  <div className="max-w-3xl mx-auto px-6 lg:px-10 py-16" data-testid="legal-page">
    <p className="text-xs tracking-[0.2em] uppercase font-bold text-sky-600 mb-3">
      Documento legale
    </p>
    <h1 className="font-heading text-4xl sm:text-5xl tracking-tight font-light text-[#032B44]">
      {title}
    </h1>
    <p className="text-slate-500 mt-2 text-sm">Ultimo aggiornamento: {updated}</p>
    <div className="mt-10 prose prose-slate max-w-none text-slate-700 leading-relaxed">
      {children}
    </div>
  </div>
);

const H2 = ({ children }) => (
  <h2 className="font-heading text-xl font-medium text-[#032B44] mt-10 mb-3">{children}</h2>
);

export const Privacy = () => (
  <LegalLayout title="Informativa Privacy" updated="Febbraio 2026">
    <p>
      La presente informativa descrive come <strong>{COMPANY.name}</strong> (di seguito "Titolare")
      tratta i dati personali degli utenti del sito web e del servizio di consegna ghiaccio EasyIce,
      in conformità al Regolamento (UE) 2016/679 ("GDPR") e al D.Lgs. 196/2003.
    </p>

    <H2>1. Titolare del trattamento</H2>
    <p>
      {COMPANY.name} · P.IVA {COMPANY.vat}<br />
      Sede: {COMPANY.address}<br />
      Legale rappresentante: {COMPANY.admin}<br />
      Email: <a href={`mailto:${COMPANY.email}`} className="text-sky-700">{COMPANY.email}</a>
    </p>

    <H2>2. Dati raccolti</H2>
    <ul className="list-disc pl-6 space-y-1">
      <li>Dati di registrazione: nome, email, password (cifrata con bcrypt), numero di telefono.</li>
      <li>Dati dell'ordine: indirizzo di consegna, fascia oraria, note.</li>
      <li>Dati di pagamento: gestiti esclusivamente da PayPal; EasyIce non accede né memorizza i dati delle carte.</li>
      <li>Dati di navigazione: cookie tecnici e di sessione (vedi informativa cookie).</li>
    </ul>

    <H2>3. Finalità e basi giuridiche</H2>
    <ul className="list-disc pl-6 space-y-1">
      <li><strong>Esecuzione del contratto</strong>: gestione ordini, consegne, fatturazione.</li>
      <li><strong>Obblighi di legge</strong>: conservazione documenti fiscali per 10 anni.</li>
      <li><strong>Interesse legittimo</strong>: sicurezza della piattaforma e prevenzione frodi.</li>
      <li><strong>Consenso</strong>: comunicazioni commerciali (solo se espressamente accettate).</li>
    </ul>

    <H2>4. Conservazione</H2>
    <p>
      I dati dell'account sono conservati finché l'account è attivo. I dati degli ordini sono
      conservati per 10 anni come richiesto dalla normativa fiscale.
    </p>

    <H2>5. Destinatari</H2>
    <p>
      I dati possono essere comunicati a PayPal (elaborazione pagamenti), Resend (invio email
      transazionali), fornitori IT (hosting e database). Nessun dato è trasferito a Paesi terzi
      senza adeguate garanzie.
    </p>

    <H2>6. Diritti dell'interessato</H2>
    <p>
      Hai diritto di accesso, rettifica, cancellazione, limitazione, portabilità e opposizione al
      trattamento dei tuoi dati. Puoi esercitare questi diritti scrivendo a{" "}
      <a href={`mailto:${COMPANY.email}`} className="text-sky-700">{COMPANY.email}</a>.
      Hai inoltre il diritto di proporre reclamo al Garante per la Protezione dei Dati Personali
      (<a href="https://www.garanteprivacy.it" target="_blank" rel="noreferrer" className="text-sky-700">garanteprivacy.it</a>).
    </p>

    <H2>7. Sicurezza</H2>
    <p>
      Utilizziamo HTTPS, hash bcrypt per le password, token di sessione firmati e accesso limitato
      ai dati. In caso di violazione dei dati informeremo tempestivamente gli utenti e le autorità
      competenti.
    </p>

    <H2>8. Modifiche</H2>
    <p>
      La presente informativa può essere aggiornata. Le modifiche sostanziali saranno comunicate
      via email o tramite banner sul sito.
    </p>
  </LegalLayout>
);

export const Terms = () => (
  <LegalLayout title="Termini e Condizioni" updated="Febbraio 2026">
    <p>
      I presenti Termini e Condizioni disciplinano l'utilizzo del servizio EasyIce fornito da{" "}
      <strong>{COMPANY.name}</strong> (di seguito "EasyIce" o "Venditore").
    </p>

    <H2>1. Identificazione del venditore</H2>
    <p>
      {COMPANY.name}<br />
      P.IVA: {COMPANY.vat}<br />
      Sede legale: {COMPANY.address}<br />
      Legale rappresentante: {COMPANY.admin}<br />
      Email: <a href={`mailto:${COMPANY.email}`} className="text-sky-700">{COMPANY.email}</a>
    </p>

    <H2>2. Oggetto del servizio</H2>
    <p>
      EasyIce vende e consegna ghiaccio alimentare (cubetti e pilé) a clienti business e privati
      nel territorio della Calabria. I prodotti disponibili, i prezzi e le unità di vendita sono
      indicati nel catalogo online.
    </p>

    <H2>3. Ordini</H2>
    <p>
      Per ordinare è necessario registrare un account e fornire un numero di telefono valido per la
      consegna. L'ordine si considera confermato quando EasyIce invia conferma tramite email o
      notifica in-app. EasyIce si riserva il diritto di non accettare ordini in caso di indisponibilità
      dei prodotti, indirizzo fuori zona o dati incompleti.
    </p>

    <H2>4. Prezzi e pagamenti</H2>
    <p>
      I prezzi sono espressi in Euro e si intendono IVA inclusa. Clienti partner possono beneficiare
      di un listino dedicato, visibile dopo l'accesso. I metodi di pagamento accettati sono:
    </p>
    <ul className="list-disc pl-6 space-y-1">
      <li><strong>PayPal</strong> (conto PayPal)</li>
      <li><strong>Carta di credito o debito</strong> (Visa, Mastercard, Amex) tramite PayPal Checkout</li>
      <li><strong>Contanti alla consegna</strong></li>
    </ul>

    <H2>5. Consegna</H2>
    <p>
      La consegna è effettuata nella data e fascia oraria (mattina 08:00–12:30 o pomeriggio 15:00–19:00)
      selezionate in fase di ordine. EasyIce contatta il cliente al numero indicato per coordinare
      l'orario esatto. In caso di assenza del cliente, EasyIce tenterà un secondo contatto; in caso
      di mancata consegna per causa del cliente, il prodotto non sarà rimborsato.
    </p>

    <H2>6. Diritto di recesso</H2>
    <p>
      Ai sensi del D.Lgs. 206/2005, il diritto di recesso <strong>non si applica</strong> ai beni
      deperibili come il ghiaccio alimentare, in quanto prodotti confezionati che rischiano di
      deteriorarsi rapidamente (art. 59, lett. d).
    </p>

    <H2>7. Garanzie e responsabilità</H2>
    <p>
      EasyIce garantisce che il ghiaccio è prodotto con acqua filtrata conforme agli standard
      alimentari. In caso di prodotto non conforme, il cliente deve comunicarlo entro 24 ore
      dalla consegna via email a {COMPANY.email}.
    </p>

    <H2>8. Trattamento dati</H2>
    <p>
      L'utilizzo del servizio implica l'accettazione della nostra Informativa Privacy.
    </p>

    <H2>9. Legge applicabile e foro competente</H2>
    <p>
      I presenti Termini sono regolati dalla legge italiana. Per qualsiasi controversia è competente
      il Foro del consumatore ove previsto, altrimenti il Foro di Castrovillari.
    </p>

    <H2>10. Modifiche</H2>
    <p>
      EasyIce può aggiornare i presenti Termini in qualsiasi momento. Le modifiche si applicano
      agli ordini effettuati successivamente alla pubblicazione.
    </p>
  </LegalLayout>
);

export const Cookies = () => (
  <LegalLayout title="Cookie Policy" updated="Febbraio 2026">
    <p>
      Il sito EasyIce utilizza unicamente <strong>cookie tecnici</strong> necessari al funzionamento
      della piattaforma. Non utilizziamo cookie di profilazione o di marketing di terze parti.
    </p>
    <H2>Cookie utilizzati</H2>
    <ul className="list-disc pl-6 space-y-1">
      <li><strong>session_token</strong> (HTTP-only, durata 7 giorni) — mantiene la sessione di login. Necessario per ordinare e usare l'area riservata.</li>
      <li><strong>easyice_cart_v1</strong> (localStorage) — salva il carrello sul tuo dispositivo finché non completi l'ordine.</li>
      <li><strong>easyice_cookie_consent</strong> (localStorage) — ricorda la tua scelta sul banner cookie.</li>
    </ul>
    <H2>Gestione</H2>
    <p>
      Puoi eliminare questi cookie in qualsiasi momento dalle impostazioni del tuo browser. La
      cancellazione del cookie di sessione ti disconnetterà dal tuo account.
    </p>
    <H2>Terze parti</H2>
    <p>
      I pagamenti sono gestiti da PayPal, che imposta propri cookie sulla sua finestra di checkout
      secondo la sua <a href="https://www.paypal.com/it/webapps/mpp/ua/privacy-full" target="_blank" rel="noreferrer" className="text-sky-700">informativa privacy</a>.
    </p>
  </LegalLayout>
);

export default Privacy;
